import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, AlertCircle, Info, ThumbsUp, ThumbsDown, Edit2, Save, X } from 'lucide-react';
import SmartFieldMapper from './SmartFieldMapper';

/**
 * Smart Field Mapper UI Component
 * ================================
 * Visual interface for reviewing and confirming field mappings
 * with drag-and-drop support and explainable AI reasoning.
 */
const SmartFieldMapperUI = ({ 
  sourceFields, 
  targetTable, 
  language = 'de',
  onMappingComplete,
  onCancel 
}) => {
  const [mapper] = useState(() => new SmartFieldMapper());
  const [mappingResult, setMappingResult] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    performMapping();
  }, [sourceFields, targetTable, language]);

  const performMapping = async () => {
    setIsLoading(true);
    try {
      // Simulate async processing
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const result = mapper.mapFields(sourceFields, targetTable, language);
      setMappingResult(result);
    } catch (error) {
      console.error('Mapping error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmMapping = (mapping) => {
    mapper.learnFromFeedback({
      type: 'confirmation',
      sourceField: mapping.sourceField,
      targetField: mapping.targetField,
      targetTable,
      language,
      confidence: mapping.confidence
    });
  };

  const handleCorrectMapping = (sourceField, newTargetField) => {
    mapper.learnFromFeedback({
      type: 'correction',
      sourceField,
      correctedTargetField: newTargetField,
      targetTable,
      language
    });

    // Update mapping result
    const updatedMappings = mappingResult.mappings.map(m => 
      m.sourceField === sourceField 
        ? { ...m, targetField: newTargetField, confidence: 1.0, reason: 'User correction' }
        : m
    );

    setMappingResult({
      ...mappingResult,
      mappings: updatedMappings
    });

    setEditingField(null);
  };

  const handleRejectMapping = (sourceField) => {
    const updatedMappings = mappingResult.mappings.filter(m => m.sourceField !== sourceField);
    const rejectedField = mappingResult.mappings.find(m => m.sourceField === sourceField);

    setMappingResult({
      ...mappingResult,
      mappings: updatedMappings,
      unmappedFields: [...mappingResult.unmappedFields, {
        sourceField: rejectedField.sourceField,
        reason: 'User rejected',
        confidence: rejectedField.confidence,
        suggestions: rejectedField.suggestions
      }]
    });

    mapper.learnFromFeedback({
      type: 'rejection',
      sourceField,
      rejectedTargetField: rejectedField.targetField,
      targetTable,
      language
    });
  };

  const handleComplete = () => {
    if (onMappingComplete) {
      onMappingComplete({
        mappings: mappingResult.mappings,
        quality: mappingResult.quality,
        unmappedFields: mappingResult.unmappedFields
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Analysiere Felder...</p>
        </div>
      </div>
    );
  }

  if (!mappingResult) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-gray-600">Fehler beim Mapping</p>
      </div>
    );
  }

  const { mappings, unmappedFields, quality } = mappingResult;

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header with Quality Score */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">
            Intelligentes Feld-Mapping
          </h2>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-sm text-gray-600">Mapping-Qualität</div>
              <div className={`text-2xl font-bold ${
                quality.score >= 0.9 ? 'text-green-600' :
                quality.score >= 0.8 ? 'text-blue-600' :
                quality.score >= 0.7 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {Math.round(quality.score * 100)}%
              </div>
              <div className="text-xs text-gray-500">{quality.grade}</div>
            </div>
          </div>
        </div>

        {/* Quality Metrics */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm text-blue-700 mb-1">Gemappte Felder</div>
            <div className="text-2xl font-bold text-blue-900">
              {quality.mappedFields} / {quality.totalFields}
            </div>
            <div className="text-xs text-blue-600">
              {Math.round(quality.coverageRatio * 100)}% Abdeckung
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm text-green-700 mb-1">Durchschn. Konfidenz</div>
            <div className="text-2xl font-bold text-green-900">
              {Math.round(quality.avgConfidence * 100)}%
            </div>
            <div className="text-xs text-green-600">
              KI-Sicherheit
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-sm text-yellow-700 mb-1">Benötigt Review</div>
            <div className="text-2xl font-bold text-yellow-900">
              {mappings.filter(m => m.requiresReview).length}
            </div>
            <div className="text-xs text-yellow-600">
              Manuelle Prüfung
            </div>
          </div>
        </div>
      </div>

      {/* Mapped Fields */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Erkannte Zuordnungen ({mappings.length})
        </h3>

        <div className="space-y-3">
          {mappings.map((mapping, index) => (
            <MappingRow
              key={index}
              mapping={mapping}
              isEditing={editingField === mapping.sourceField}
              onEdit={() => setEditingField(mapping.sourceField)}
              onSave={(newTarget) => handleCorrectMapping(mapping.sourceField, newTarget)}
              onCancel={() => setEditingField(null)}
              onConfirm={() => handleConfirmMapping(mapping)}
              onReject={() => handleRejectMapping(mapping.sourceField)}
              targetTable={targetTable}
              mapper={mapper}
            />
          ))}
        </div>
      </div>

      {/* Unmapped Fields */}
      {unmappedFields.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Nicht zugeordnete Felder ({unmappedFields.length})
          </h3>

          <div className="space-y-3">
            {unmappedFields.map((field, index) => (
              <UnmappedFieldRow
                key={index}
                field={field}
                onMap={(targetField) => {
                  // Move to mapped fields
                  const newMapping = {
                    sourceField: field.sourceField,
                    targetField,
                    confidence: 1.0,
                    reason: 'User mapped',
                    requiresReview: false
                  };

                  setMappingResult({
                    ...mappingResult,
                    mappings: [...mappingResult.mappings, newMapping],
                    unmappedFields: unmappedFields.filter((_, i) => i !== index)
                  });
                }}
                targetTable={targetTable}
                mapper={mapper}
              />
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t">
        <button
          onClick={onCancel}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Abbrechen
        </button>

        <button
          onClick={handleComplete}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
        >
          Mapping übernehmen
          <ArrowRight className="ml-2 h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * Individual mapping row component
 */
const MappingRow = ({ 
  mapping, 
  isEditing, 
  onEdit, 
  onSave, 
  onCancel, 
  onConfirm, 
  onReject,
  targetTable,
  mapper
}) => {
  const [selectedTarget, setSelectedTarget] = useState(mapping.targetField);
  const semanticLayer = mapper.semanticLayers[targetTable];

  const confidenceColor = 
    mapping.confidence >= 0.9 ? 'text-green-600 bg-green-50' :
    mapping.confidence >= 0.8 ? 'text-blue-600 bg-blue-50' :
    mapping.confidence >= 0.7 ? 'text-yellow-600 bg-yellow-50' : 'text-red-600 bg-red-50';

  return (
    <div className={`border rounded-lg p-4 ${
      mapping.requiresReview ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200 bg-white'
    }`}>
      <div className="flex items-center justify-between">
        {/* Source Field */}
        <div className="flex-1">
          <div className="font-medium text-gray-900">{mapping.sourceField}</div>
          <div className="text-sm text-gray-500">Quellfeld</div>
        </div>

        {/* Arrow */}
        <ArrowRight className="h-5 w-5 text-gray-400 mx-4" />

        {/* Target Field */}
        <div className="flex-1">
          {isEditing ? (
            <select
              value={selectedTarget}
              onChange={(e) => setSelectedTarget(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {semanticLayer.fields.map(field => (
                <option key={field} value={field}>{field}</option>
              ))}
            </select>
          ) : (
            <>
              <div className="font-medium text-gray-900">{mapping.targetField}</div>
              <div className="text-sm text-gray-500">Zielfeld</div>
            </>
          )}
        </div>

        {/* Confidence Score */}
        <div className={`ml-4 px-3 py-1 rounded-full text-sm font-medium ${confidenceColor}`}>
          {Math.round(mapping.confidence * 100)}%
        </div>

        {/* Actions */}
        <div className="ml-4 flex items-center space-x-2">
          {isEditing ? (
            <>
              <button
                onClick={() => onSave(selectedTarget)}
                className="p-2 text-green-600 hover:bg-green-50 rounded"
                title="Speichern"
              >
                <Save className="h-4 w-4" />
              </button>
              <button
                onClick={onCancel}
                className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                title="Abbrechen"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onEdit}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                title="Bearbeiten"
              >
                <Edit2 className="h-4 w-4" />
              </button>
              <button
                onClick={onConfirm}
                className="p-2 text-green-600 hover:bg-green-50 rounded"
                title="Bestätigen"
              >
                <ThumbsUp className="h-4 w-4" />
              </button>
              <button
                onClick={onReject}
                className="p-2 text-red-600 hover:bg-red-50 rounded"
                title="Ablehnen"
              >
                <ThumbsDown className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Reasoning */}
      <div className="mt-3 flex items-start space-x-2 text-sm text-gray-600">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          <span className="font-medium">Begründung:</span> {mapping.reason}
        </div>
      </div>

      {/* Alternative Suggestions */}
      {mapping.suggestions && mapping.suggestions.length > 1 && (
        <div className="mt-2 text-sm text-gray-500">
          <span className="font-medium">Alternativen:</span>{' '}
          {mapping.suggestions.slice(1, 3).map((s, i) => (
            <span key={i}>
              {s.targetField} ({Math.round(s.confidence * 100)}%)
              {i < 1 && ', '}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Unmapped field row component
 */
const UnmappedFieldRow = ({ field, onMap, targetTable, mapper }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const semanticLayer = mapper.semanticLayers[targetTable];

  return (
    <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="font-medium text-gray-900">{field.sourceField}</div>
          <div className="text-sm text-gray-500">{field.reason}</div>
        </div>

        <button
          onClick={() => setShowSuggestions(!showSuggestions)}
          className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
        >
          {showSuggestions ? 'Verbergen' : 'Manuell zuordnen'}
        </button>
      </div>

      {showSuggestions && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="text-sm font-medium text-gray-700 mb-2">Mögliche Zuordnungen:</div>
          <div className="grid grid-cols-2 gap-2">
            {field.suggestions?.slice(0, 4).map((suggestion, index) => (
              <button
                key={index}
                onClick={() => onMap(suggestion.targetField)}
                className="px-3 py-2 text-left text-sm border border-gray-200 rounded hover:bg-white hover:border-blue-300"
              >
                <div className="font-medium">{suggestion.targetField}</div>
                <div className="text-xs text-gray-500">
                  {Math.round(suggestion.confidence * 100)}% Konfidenz
                </div>
              </button>
            ))}
          </div>

          <div className="mt-2">
            <select
              onChange={(e) => e.target.value && onMap(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
              defaultValue=""
            >
              <option value="">Oder wähle ein anderes Feld...</option>
              {semanticLayer.fields.map(field => (
                <option key={field} value={field}>{field}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartFieldMapperUI;

